// src/components/TodoWidget.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Paper, Typography, List, ListItem, Checkbox, ListItemText,
    TextField, IconButton, Box, CircularProgress, Tooltip, Stack, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Chip,
    Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { 
    AddCircleOutline as AddIcon, 
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { it } from 'date-fns/locale';
import { format, isToday, parseISO, isPast, startOfToday } from 'date-fns';
import api from '../utils/api';
import Swal from 'sweetalert2';

function EditTodoDialog({ open, onClose, todo, onSave }) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [dueDate, setDueDate] = useState(null);

    useEffect(() => {
        if (todo) {
            setTitle(todo.title || '');
            setDescription(todo.description || '');
            setDueDate(todo.dueDate ? parseISO(todo.dueDate) : startOfToday());
        }
    }, [todo]);

    const handleSave = () => {
        if (title.trim()) {
            onSave({ ...todo, title, description, dueDate });
        }
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Modifica Attività</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Titolo Attività"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                    />
                    <TextField
                        margin="dense"
                        label="Descrizione (opzionale)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                    />
                    <DatePicker
                        label="Scadenza"
                        value={dueDate}
                        onChange={setDueDate}
                        minDate={startOfToday()}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Annulla</Button>
                <Button onClick={handleSave} variant="contained">Salva Modifiche</Button>
            </DialogActions>
        </Dialog>
    );
}


// ===================================================================
//  COMPONENTE PRINCIPALE DEL WIDGET
// ===================================================================
function TodoWidget() {
    const [todos, setTodos] = useState([]);
    const [newTodoTitle, setNewTodoTitle] = useState('');
    const [newTodoDescription, setNewTodoDescription] = useState('');
    const [newTodoDueDate, setNewTodoDueDate] = useState(startOfToday());
    
    const [loading, setLoading] = useState(true);
    const [editingTodo, setEditingTodo] = useState(null);
    // --- NUOVO STATO: per controllare l'apertura dell'accordion ---
    const [showCompleted, setShowCompleted] = useState(false);

    const fetchTodos = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.get('/todos');
            setTodos(data);
        } catch (error) {
            console.error("Errore nel recupero dei todo:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTodos();
    }, [fetchTodos]);

    // ... (le funzioni handle... rimangono invariate)
    const handleToggleComplete = async (id) => {
        setTodos(prev => prev.map(t => t.id === id ? { ...t, completed: !t.completed } : t));
        try {
            await api.put(`/todos/${id}/complete`);
        } catch (error) {
            fetchTodos();
        }
    };

    const handleAddTodo = async (e) => {
        e.preventDefault();
        if (!newTodoTitle.trim()) return;
        
        const payload = { 
            title: newTodoTitle.trim(), 
            description: newTodoDescription.trim(),
            dueDate: newTodoDueDate, 
        };
        
        setNewTodoTitle('');
        setNewTodoDescription('');
        setNewTodoDueDate(startOfToday());
        
        try {
            await api.post('/todos', payload);
            await fetchTodos();
        } catch (error) {
            console.error("Errore aggiunta todo:", error);
        }
    };

    const handleUpdateTodo = async (updatedTodo) => {
        try {
            await api.put(`/todos/${updatedTodo.id}`, updatedTodo);
            setEditingTodo(null);
            await fetchTodos();
        } catch (error) {
            console.error("Errore aggiornamento todo:", error);
        }
    };

    const handleDeleteTodo = async (id) => {
        Swal.fire({
            title: 'Sei sicuro?',
            text: "Questa azione non può essere annullata!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sì, elimina!',
            cancelButtonText: 'Annulla'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await api.delete(`/todos/${id}`);
                    await fetchTodos();
                    Swal.fire({
                        title: 'Eliminato!',
                        text: 'L\'attività è stata eliminata.',
                        icon: 'success',
                        timer: 1500,
                        position: 'top-end',
                        showConfirmButton: false,
                        toast: true,
                    });
                } catch (error) {
                    console.error("Errore nella cancellazione del todo:", error);
                };
            }
        });
    };

    // --- LOGICA MODIFICATA: Separa le attività attive da quelle completate ---
    const { activeTodos, completedTodos } = useMemo(() => {
        const active = [];
        const completed = [];
        
        todos.forEach(todo => {
            if (todo.completed) {
                completed.push(todo);
            } else {
                active.push(todo);
            }
        });

        // Ordina le attività attive per data di scadenza
        active.sort((a, b) => {
            const dateA = a.dueDate ? new Date(a.dueDate) : null;
            const dateB = b.dueDate ? new Date(b.dueDate) : null;
            if (dateA && !dateB) return -1;
            if (!dateA && dateB) return 1;
            if (!dateA && !dateB) return 0;
            return dateA - dateB;
        });

        return { activeTodos: active, completedTodos: completed };
    }, [todos]);

    const renderDueDate = (dueDateStr) => {
        if (!dueDateStr) return null;
        const date = parseISO(dueDateStr);
        
        const isOverdue = isPast(date) && !isToday(date);
        const isTodayTask = isToday(date);
        
        let color, label;
        if (isOverdue) {
            color = "error";
            label = `Scaduto il ${format(date, 'd MMM', { locale: it })}`;
        } else if (isTodayTask) {
            color = "warning";
            label = 'Scade Oggi';
        } else {
            color = "primary";
            label = `Scade il ${format(date, 'd MMM', { locale: it })}`;
        }
        
        return <Chip label={label} color={color} size="small" variant="outlined" />;
    };

    // --- NUOVA FUNZIONE: per renderizzare un singolo item, evitando duplicazione ---
    const renderTodoItem = (todo) => (
        <ListItem
            key={todo.id}
            sx={{ 
                mb: 1, 
                bgcolor: todo.completed ? 'action.hover' : 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                flexWrap: 'wrap'
            }}
            secondaryAction={
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Modifica">
                        <IconButton size="small" onClick={() => setEditingTodo(todo)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Elimina">
                        <IconButton size="small" edge="end" onClick={() => handleDeleteTodo(todo.id)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            }
        >
            <Checkbox
                edge="start"
                checked={!!todo.completed}
                onChange={() => handleToggleComplete(todo.id)}
            />
            <ListItemText
                primary={
                    <Typography sx={{ textDecoration: todo.completed ? 'line-through' : 'none' }}>
                        {todo.title}
                    </Typography>
                }
                secondary={todo.description}
                sx={{ 
                    color: todo.completed ? 'text.disabled' : 'text.secondary',
                    pr: '80px'
                }}
            />
             <Box sx={{ width: '100%', pl: '42px', pt: 0.5 }}>
                {renderDueDate(todo.dueDate)}
            </Box>
        </ListItem>
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* ... (Header e form di aggiunta rimangono invariati) ... */}
                 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Le Mie Attività</Typography>
                    <Tooltip title="Aggiorna lista">
                        <IconButton onClick={fetchTodos} size="small"><RefreshIcon /></IconButton>
                    </Tooltip>
                </Box>
                <Box component="form" onSubmit={handleAddTodo} sx={{ mb: 2 }}>
                    <Stack spacing={1.5}>
                        <TextField 
                            value={newTodoTitle} 
                            onChange={(e) => setNewTodoTitle(e.target.value)} 
                            label="Nuova attività..." 
                            variant="outlined" 
                            size="small"
                        />
                        <TextField 
                            value={newTodoDescription} 
                            onChange={(e) => setNewTodoDescription(e.target.value)} 
                            label="Descrizione (opzionale)" 
                            variant="outlined" 
                            size="small"
                        />
                         <Stack direction="row" spacing={1} alignItems="center">
                            <DatePicker
                                label="Scadenza"
                                value={newTodoDueDate}
                                onChange={setNewTodoDueDate}
                                minDate={startOfToday()}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                            <IconButton type="submit" color="primary" sx={{p: '10px'}}><AddIcon /></IconButton>
                        </Stack>
                    </Stack>
                </Box>
                <Divider />

                <Box sx={{ flexGrow: 1, overflowY: 'auto', mt: 1 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                    ) : todos.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography color="text.secondary">Nessuna attività. Complimenti! 🎉</Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Renderizza la lista delle attività da fare */}
                            <List dense>
                                {activeTodos.map(renderTodoItem)}
                            </List>
                            
                            {/* --- NUOVA SEZIONE: Accordion per le attività completate --- */}
                            {completedTodos.length > 0 && (
                                <Accordion 
                                    sx={{ mt: 2 }} 
                                    expanded={showCompleted} 
                                    onChange={() => setShowCompleted(!showCompleted)}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography sx={{ mr: 1 }}>Attività Completate</Typography>
                                        <Chip label={completedTodos.length} size="small" />
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <List dense>
                                            {completedTodos.map(renderTodoItem)}
                                        </List>
                                    </AccordionDetails>
                                </Accordion>
                            )}
                        </>
                    )}
                </Box>
            </Paper>

            <EditTodoDialog
                open={!!editingTodo}
                onClose={() => setEditingTodo(null)}
                todo={editingTodo}
                onSave={handleUpdateTodo}
            />
        </LocalizationProvider>
    );
}

export default TodoWidget;